import React, { useState, useEffect } from 'react';
import api from '../../api/axios'; // Use authenticated api instance instead of direct axios
import supabase from '../../api/supabase';
import {
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/solid';
import FileUpload from '../../components/ui/form/FileUpload';

const statusColors = {
  paid: 'bg-green-100 text-green-800',
  due: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-blue-100 text-blue-800',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
  awaiting_verification: 'bg-blue-100 text-blue-800',
  // Frontend-only statuses
  'Pending Validation': 'bg-blue-100 text-blue-800',
  Requested: 'bg-yellow-50 text-yellow-700', // Kept for outstanding requests if any
};

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectStats, setProjectStats] = useState(null);

  const [proofFiles, setProofFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [showHelp, setShowHelp] = useState({});
  const [filters, setFilters] = useState({ status: 'All', phase: 'All' });

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use the authenticated api instance instead of direct axios
        const { data } = await api.get('/payments');
        // Fix: Access the nested data property and ensure it's always an array
        const paymentsData = data.data || data || [];
        setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      } catch (err) {
        console.error("Failed to fetch payments:", err);
        setError("Could not load payments. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    const fetchProjectStats = async () => {
      try {
        const { data } = await api.get('/dashboard/stats');
        setProjectStats(data);
      } catch (err) {
        console.error("Failed to fetch project stats:", err);
        // Don't fail the whole page if stats fail
      }
    };

    fetchPayments();
    fetchProjectStats();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Utility to format numbers in Indian system (e.g., 1 crore 30 lakh)
  function formatIndianPrice(num) {
    if (!num) return 'N/A';
    num = Number(num);
    if (isNaN(num)) return num;
    if (num >= 10000000) {
      const crore = Math.floor(num / 10000000);
      const lakh = Math.floor((num % 10000000) / 100000);
      return `${crore} crore${lakh ? ' ' + lakh + ' lakh' : ''}`;
    } else if (num >= 100000) {
      const lakh = Math.floor(num / 100000);
      const thousand = Math.floor((num % 100000) / 1000);
      return `${lakh} lakh${thousand ? ' ' + thousand + ' thousand' : ''}`;
    } else if (num >= 1000) {
      const thousand = Math.floor(num / 1000);
      return `${thousand} thousand`;
    }
    return num.toString();
  }

  // Filtered transactions
  const filteredTransactions = payments.filter(tx => {
    const statusMatch = filters.status === 'All' || tx.status === filters.status;
    const phaseMatch = filters.phase === 'All' || tx.phase === filters.phase;
    return statusMatch && phaseMatch;
  });

  // Unique phases for filter
  const phaseOptions = ['All', ...Array.from(new Set(payments.map(tx => tx.phase)))];
  const statusOptions = ['All', ...new Set(payments.map(tx => tx.status))];

  // Handle file upload per outstanding payment
  const handleProofUpload = (id, file) => {
    setProofFiles(prev => ({ ...prev, [id]: file }));
  };

  // Submit proof for outstanding payment
  const handleSubmitProof = async (id) => {
    setUploading(prev => ({ ...prev, [id]: true }));
    try {
      let proofUrl = '';
      const file = proofFiles[id];
      if (file) {
        // Upload to Supabase Storage
        const fileExt = file.name.split('.').pop();
        const filePath = `payment-proofs/${id}-${Date.now()}.${fileExt}`;
        let { error: uploadError } = await supabase.storage.from('payment-proofs').upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
        if (uploadError) throw uploadError;
        // Get public URL
        const { data: publicUrlData } = supabase.storage.from('payment-proofs').getPublicUrl(filePath);
        proofUrl = publicUrlData?.publicUrl || '';
        if (!proofUrl) throw new Error('Failed to get public URL for uploaded file');
      }
      // Call backend endpoint
      await api.post(`/payments/${id}/submit-proof`, {
        proof_url: proofUrl,
        // Add transaction_id if you have a field for it
      });
      setPayments(prev => prev.map(req =>
        req.id === id
          ? { ...req, status: 'awaiting_verification', receipt_url: proofUrl }
          : req
      ));
    } catch (err) {
      alert('Failed to submit payment proof. ' + (err.message || 'Please try again.'));
    } finally {
      setUploading(prev => ({ ...prev, [id]: false }));
    }
  };

  // Help dialog per outstanding payment
  const openHelp = (id) => setShowHelp(prev => ({ ...prev, [id]: true }));
  const closeHelp = (id) => setShowHelp(prev => ({ ...prev, [id]: false }));

  // Show upload for all relevant pending statuses
  const isPendingStatus = (status) => {
    if (!status) return false;
    const normalized = status.toLowerCase();
    return [
      'pending', 'due', 'requested', 'awaiting_verification', 'pending validation'
    ].includes(normalized);
  };

  // Add handleMarkAsPaid function
  const handleMarkAsPaid = async (id) => {
    setUploading(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/payments/${id}/mark-as-paid`);
      setPayments(prev => prev.map(req =>
        req.id === id
          ? { ...req, status: 'awaiting_confirmation' }
          : req
      ));
    } catch (err) {
      alert('Failed to mark as paid. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [id]: false }));
    }
  };

  // Outstanding payments filter
  const outstandingPayments = payments.filter(
    p => [
      'pending', 'due', 'requested', 'awaiting_verification', 'pending validation', 'awaiting_confirmation'
    ].includes((p.status || '').toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Payments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Payments</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen font-body">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-display-bold text-gray-900 mb-2">Payment History</h1>
          <p className="text-lg text-gray-600">
            Track all your project payments, invoices, and transaction history.
          </p>
        </div>

        {/* Payment Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sale Price</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(projectStats?.soldCost || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">{formatIndianPrice(projectStats?.soldCost)}</p>
              </div>
              <CreditCardIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Paid</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}
                </p>
                <p className="text-xs text-gray-500 mt-1">{formatIndianPrice(payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-xl font-bold text-yellow-600">
                  {formatCurrency(payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}
                </p>
                <p className="text-xs text-gray-500 mt-1">{formatIndianPrice(payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}</p>
              </div>
              <ClockIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-xl font-bold text-gray-900">{payments.length}</p>
              </div>
              <BanknotesIcon className="h-8 w-8 text-palamuru-blue" />
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden mb-10">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Payments</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.length > 0 ? (
                  payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{payment.description}</div>
                          <div className="text-sm text-gray-500">#{payment.transaction_id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{formatCurrency(payment.amount)}</div>
                          <div className="text-xs text-gray-500">{formatIndianPrice(payment.amount)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(payment.status)}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                            {payment.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {payment.invoice_url && (
                          <a
                            href={payment.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-palamuru-blue hover:text-palamuru-gold flex items-center gap-1"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Invoice
                          </a>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <CreditCardIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Payments Found</h3>
                        <p className="text-gray-500">You haven't made any payments yet.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Outstanding Payments Section */}
        <section className="mb-12">
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8 mb-8">
            <h2 className="font-display-semibold text-xl text-gray-900 mb-4">Outstanding Payments</h2>
            {outstandingPayments.length === 0 ? (
              <div className="text-center text-gray-500 font-body">No outstanding payments. All dues are clear!</div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {outstandingPayments.map(req => (
                  <div key={req.id} className="bg-white rounded-2xl shadow-soft border border-yellow-100 p-6 flex flex-col gap-3 relative">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-palamuru-saffron/80 to-palamuru-gold/80 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" /></svg>
                      </div>
                      <div>
                        <div className="font-display-semibold text-lg text-gray-900">{req.phase}</div>
                        <div className="text-xs text-gray-500">Due: {new Date(req.due_date).toLocaleDateString()}</div>
                      </div>
                      <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[req.status]}`}>{req.status === 'awaiting_verification' ? 'Awaiting Verification' : req.status}</span>
                    </div>
                    <div className="flex flex-col gap-1 mb-2">
                      <div className="text-lg font-display-bold text-palamuru-blue">₹{Number(req.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                      <div className="text-xs text-gray-500">{formatIndianPrice(req.amount)}</div>
                      {req.description && <p className="text-xs text-gray-500">{req.description}</p>}
                    </div>
                    
                    {isPendingStatus(req.status) && (
                      <>
                        <button
                          className="bg-palamuru-blue text-white font-body-semibold px-6 py-3 rounded-xl shadow-soft hover:bg-palamuru-blue/90 transition-all disabled:opacity-60 mt-2"
                          disabled={uploading[req.id]}
                          onClick={() => handleMarkAsPaid(req.id)}
                        >
                          {uploading[req.id] ? 'Submitting...' : 'Mark as Paid'}
                        </button>
                        <div className="text-xs text-gray-500 mt-1">Once you have paid, click 'Mark as Paid'. Admin will verify and confirm your payment.</div>
                      </>
                    )}
                    {req.status === 'awaiting_verification' && (
                      <div className="text-xs text-blue-700 font-semibold mt-2">Your payment is sent for verification. You will be notified once it is reviewed by admin.</div>
                    )}
                    
                    {req.receipt_url && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">Proof uploaded:</span>
                        <a href={req.receipt_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-palamuru-blue underline">View</a>
                      </div>
                    )}
                    <button
                      className="mt-2 text-xs text-palamuru-blue hover:text-palamuru-gold font-body-semibold underline self-start"
                      onClick={() => openHelp(req.id)}
                    >
                      Need help with payment?
                    </button>
                    {showHelp[req.id] && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full relative">
                          <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-700" onClick={() => closeHelp(req.id)}>
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                          <h3 className="font-display-semibold text-lg text-gray-900 mb-2">Need help with payment?</h3>
                          <p className="text-gray-600 font-body mb-4">If you have any questions or issues regarding your payment, please contact our support team:</p>
                          <div className="mb-2">
                            <div className="font-body-semibold text-gray-800">Phone:</div>
                            <div className="text-gray-700">+91 98765 43210</div>
                          </div>
                          <div className="mb-2">
                            <div className="font-body-semibold text-gray-800">Email:</div>
                            <div className="text-gray-700">support@palamuru.com</div>
                          </div>
                          <button
                            className="mt-4 bg-palamuru-blue text-white font-body-semibold px-6 py-2 rounded-xl shadow-soft hover:bg-palamuru-blue/90 transition-all"
                            onClick={() => closeHelp(req.id)}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* All Transactions Section */}
        <section>
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8">
            <h2 className="font-display-semibold text-xl text-gray-900 mb-4">All Transactions</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phase</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.length > 0 ? (
                    payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">{payment.transaction_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{new Date(payment.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{payment.phase}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{formatCurrency(payment.amount)}</div>
                            <div className="text-xs text-gray-500">{formatIndianPrice(payment.amount)}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>{payment.status}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {payment.receipt_url ? (
                            <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer" className="text-palamuru-blue underline">Receipt</a>
                          ) : (
                            <span className="text-gray-400">No Receipt</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">No transactions found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Trust-building Section */}
        <div className="bg-gradient-to-r from-palamuru-blue/5 to-palamuru-gold/5 rounded-2xl p-6 text-center mt-10">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg className="w-6 h-6 text-palamuru-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-1.104.896-2 2-2s2 .896 2 2-.896 2-2 2-2-.896-2-2-2zm0 0V7m0 4v4m0 0c0 1.104-.896 2-2 2s-2-.896-2-2 .896-2 2-2 2 .896 2 2z" /></svg>
            <span className="font-display-semibold text-gray-800">Palamuru verifies every payment before proceeding to the next construction phase. Your money is always accounted for.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsPage; 